// Room impulse-response synthesis.
//
// One stereo IR is built per room state from two physics curves the backend
// already serves: a modal transfer-function magnitude (0..200 Hz) and a set of
// RT60 decay times (per-frequency below 200 Hz, per third-octave above). The IR
// has three layers:
//
//   (a) Modal band  (≤ 200 Hz): a damped sinusoid per freq-response point.
//       Amplitude = the room's transfer-function magnitude (the EQ); decay =
//       the per-frequency RT60 (the Q-derived ring). L = R (coherent), because
//       LF wavelengths dwarf ear spacing — coherent bass locks the phantom
//       center. THIS is where the audible "boom vs. tight" lives.
//
//   (b) Diffuse tail (> 200 Hz): per third-octave velvet noise, bandpassed,
//       decayed by the band RT60, RMS-normalized to a target spectral envelope.
//       Independent seeded L/R → a decorrelated stereo reverb tail.
//
//   (c) Dry/wet energy split to a target direct-to-reverberant ratio, then a
//       peak cap. Untouched from the legacy calibration.
//
// THE ONE NON-PORT CHANGE — modal re-referencing (see normalizeModal):
// the voxel freq_response is absolute pressure (~1e-7 Pa), not the ~0 dB
// relative curve the tail target assumes. Used raw, the LF would sit ~140 dB
// below the HF (silent). We instead pin the modal band's RMS to a
// spectral-density-matched target, anchored to the untreated state and shared
// with the treated state. That (1) makes EQed LF perceptually level with HF,
// (2) is robust to how many points the freq_response API returns, and
// (3) preserves the before/after LF-level difference plus modal coloration.

import type { RoomAcoustics, SynthOptions, SynthResult } from './types';
import { mulberry32, seedFromRoom } from './prng';
import {
  AIR_ABSORPTION_COEFF,
  IR_LENGTH_SEC,
  IR_PEAK_TARGET,
  LN10_3,
  MODAL_ANCHOR_LO_HZ,
  MODAL_CROSSOVER_HZ,
  MODAL_RT60_CEIL_S,
  MODAL_RT60_FLOOR_S,
  SPEED_OF_SOUND,
  THIRD_OCTAVE_CENTERS,
  THIRD_OCTAVE_Q,
  VELVET_DENSITY,
  bandsToArrays,
  biquadBandpassInPlace,
  clamp,
  geomeanInBand,
  interpLogFreq,
  tailTarget,
  velvetNoise,
} from './dsp';

const ATTACK_TAU_S = 0.005; // 5 ms soft mode onset, keeps the t=0 LF spike out.

/**
 * Build the modal contribution (mono) into `out`, using the room's
 * re-referenced transfer-function magnitude as per-mode amplitude and the
 * per-frequency RT60 as per-mode decay. Returns nothing; caller normalizes.
 *
 * PERF: this is a bank of ~hundreds of damped sinusoids (one per freq_response
 * point — production sends 800, see admin_optimize.py's FR_N_POINTS) each
 * rendered over a ~2s buffer, i.e. an O(N_modes * N_samples) inner loop. The
 * naive form (git history) called Math.cos + 2x Math.exp *per sample per
 * mode* — profiling measured that at ~800 * 88200 * 3 ≈ 212M transcendental
 * calls for one state's IR (~12s in a real Chromium tab on the profiling
 * machine), dominating the whole "Listen" pipeline by 2-3 orders of magnitude
 * over everything else (fetch, decode, native convolution, peak scan)
 * combined.
 *
 * Fix: a damped sinusoid amp*env(t)*cos(w*t+phase) is a geometric recurrence.
 * Advancing a unit-magnitude phasor (cRe, cIm) by the fixed per-sample rotation
 * (cosStep, sinStep) = (cos(w*dt), sin(w*dt)) reproduces cos(w*t+phase) exactly
 * (up to float rounding); same for the exponential envelope and attack ramp via
 * their own per-sample multiplicative step. This drops the per-sample cost to a
 * handful of multiply-adds — the Math.cos/Math.exp calls move OUTSIDE the
 * sample loop (O(N_modes), not O(N_modes * N_samples)). Double-precision
 * rotation error accumulates as O(sqrt(numSamples) * eps) ≈ 1e-14 relative
 * over 88k steps — far below the 16-bit audio noise floor, so this is not an
 * approximation in any audible sense; __tests__/irSynthEquivalence.test.ts
 * asserts the recurrence reproduces the naive trig sum to 1e-9 relative error.
 */
function buildModalMono(
  out: Float32Array,
  room: RoomAcoustics,
  sampleRate: number,
  rand: () => number,
): void {
  const { freqs, mag } = room.freqResponse;
  const N = freqs.length;
  if (N === 0) return;

  // Re-reference: divide by the geometric-mean magnitude near the crossover so
  // the band's level is unit-anchored and the absolute Pa scale washes out.
  const anchor = geomeanInBand(freqs, mag, MODAL_ANCHOR_LO_HZ, MODAL_CROSSOVER_HZ);
  const invAnchor = anchor > 0 ? 1 / anchor : 0;

  const numSamples = out.length;
  const dt = 1 / sampleRate;
  const fine = room.rt60Fine;
  // Attack shape is mode-independent, so its per-sample step is hoisted out of
  // the mode loop (one Math.exp call total, not one per mode).
  const attackStep = Math.exp(-dt / ATTACK_TAU_S);

  for (let i = 0; i < N; i++) {
    const f = freqs[i];
    if (f <= 0 || f > MODAL_CROSSOVER_HZ) continue;
    const rt60 = clamp(
      interpLogFreq(fine.freqs, fine.rt60, f),
      MODAL_RT60_FLOOR_S,
      MODAL_RT60_CEIL_S,
    );
    const amp = mag[i] * invAnchor;
    if (amp <= 0) continue;
    const phase = rand() * 2 * Math.PI;
    const omega = 2 * Math.PI * f;
    const decayRate = LN10_3 / rt60;

    // Per-mode recurrence constants — O(1) transcendental calls per mode.
    const omegaDt = omega * dt;
    const cosStep = Math.cos(omegaDt);
    const sinStep = Math.sin(omegaDt);
    const envStep = Math.exp(-decayRate * dt);

    let cRe = Math.cos(phase);
    let cIm = Math.sin(phase);
    let env = 1;
    let attackEnv = 1; // exp(-t/tau) at t=0

    for (let n = 0; n < numSamples; n++) {
      const attack = 1 - attackEnv;
      out[n] += amp * attack * env * cRe;

      // Advance the phasor + envelopes to sample n+1 via their fixed step —
      // exactly reproduces cos(omega*t+phase), exp(-decayRate*t), exp(-t/tau).
      const nextRe = cRe * cosStep - cIm * sinStep;
      cIm = cRe * sinStep + cIm * cosStep;
      cRe = nextRe;
      env *= envStep;
      attackEnv *= attackStep;
    }
  }
}

/** RMS of a buffer. */
function rms(buf: Float32Array): number {
  let s = 0;
  for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
  return Math.sqrt(s / Math.max(buf.length, 1));
}

/**
 * Spectral-density-matched RMS target for the modal band: the tail normalizes
 * each third-octave to `tailTarget(fc)`, so the modal band — which spans
 * `nModalThirds` third-octaves — should carry sqrt(nModalThirds)·T_crossover of
 * RMS to sit at the same per-band level as the tail.
 */
function modalRmsTarget(room: RoomAcoustics): number {
  const lo = Math.max(20, room.freqResponse.freqs[0] || 20);
  let n = 0;
  for (const fc of THIRD_OCTAVE_CENTERS) {
    if (fc >= lo && fc <= MODAL_CROSSOVER_HZ) n++;
  }
  return tailTarget(MODAL_CROSSOVER_HZ) * Math.sqrt(Math.max(n, 1));
}

/**
 * Add the diffuse velvet-noise tail (> crossover) into wetL/wetR with
 * independent seeded L/R noise and per-band RMS normalization to the target
 * spectral envelope.
 */
function buildDiffuseTail(
  wetL: Float32Array,
  wetR: Float32Array,
  room: RoomAcoustics,
  sampleRate: number,
  baseSeed: number,
): void {
  const numSamples = wetL.length;
  const bands = bandsToArrays(room.rt60Bands);
  const maxBackendFreq = bands.freqs.length ? bands.freqs[bands.freqs.length - 1] : 0;
  const nyquist = sampleRate * 0.5;

  for (const fc of THIRD_OCTAVE_CENTERS) {
    if (fc < MODAL_CROSSOVER_HZ) continue; // tail starts at the crossover band
    if (fc >= nyquist * 0.9) break;

    const rt60 = interpLogFreq(bands.freqs, bands.rt60, fc);
    if (!rt60 || rt60 <= 0.01) continue;

    let decayRate = LN10_3 / rt60;
    if (fc > maxBackendFreq && maxBackendFreq > 0) {
      decayRate += AIR_ABSORPTION_COEFF * (fc * fc - maxBackendFreq * maxBackendFreq) * SPEED_OF_SOUND;
    }

    const fcInt = Math.round(fc) | 0;
    const seedL = (baseSeed ^ Math.imul(fcInt, 0x9e3779b1)) >>> 0;
    const seedR = (baseSeed ^ Math.imul(fcInt, 0x85ebca6b) ^ 0xa1b2c3d4) >>> 0;
    const bandL = velvetNoise(numSamples, sampleRate, VELVET_DENSITY, mulberry32(seedL));
    const bandR = velvetNoise(numSamples, sampleRate, VELVET_DENSITY, mulberry32(seedR));
    biquadBandpassInPlace(bandL, fc, sampleRate, THIRD_OCTAVE_Q);
    biquadBandpassInPlace(bandR, fc, sampleRate, THIRD_OCTAVE_Q);

    // Same recurrence trick as the modal band: exp(-decayRate*i/sampleRate) is
    // envStep^i, so step it multiplicatively instead of calling Math.exp per
    // sample (see buildModalMono's comment for the equivalence argument).
    let sumSq = 0;
    const envStep = Math.exp(-decayRate / sampleRate);
    let env = 1;
    for (let i = 0; i < numSamples; i++) {
      bandL[i] *= env;
      bandR[i] *= env;
      sumSq += bandL[i] * bandL[i] + bandR[i] * bandR[i];
      env *= envStep;
    }
    const rmsAvg = Math.sqrt(sumSq / (2 * numSamples));
    if (rmsAvg < 1e-30) continue;

    const gain = tailTarget(fc) / rmsAvg;
    for (let i = 0; i < numSamples; i++) {
      wetL[i] += gain * bandL[i];
      wetR[i] += gain * bandR[i];
    }
  }
}

/**
 * Target direct-to-reverberant ratio (dB) for a state, by panel count.
 * Calibrated: n=0 (untreated) → −3 dB; n=30 (professional) → +7.4 dB, linear in
 * between and clamped. Working in DRR dB (not a peak split) keeps the direct
 * sound audible — it is the perceptual cue listeners use for distance/clarity.
 */
export function drrForState(nProducts: number): number {
  const t = clamp((Number(nProducts) || 0) / 30, 0, 1);
  return -3 + 10.4 * t;
}

/**
 * Synthesize one stereo room IR.
 *
 * Pass `opts.modalScale` to force the modal-band scale (the treated state
 * inherits the untreated state's scale so the before/after LF-level delta
 * survives). Omit it for the reference state — the function self-normalizes and
 * returns the scale it computed.
 */
export function synthesizeIR(
  ctx: BaseAudioContext,
  room: RoomAcoustics,
  opts: SynthOptions = {},
): SynthResult {
  const sampleRate = ctx.sampleRate;
  const irLengthSec = opts.irLengthSec ?? IR_LENGTH_SEC;
  const numSamples = Math.max(1, Math.ceil(irLengthSec * sampleRate));
  const buffer = ctx.createBuffer(2, numSamples, sampleRate);
  const left = buffer.getChannelData(0);
  const right = buffer.getChannelData(1);

  const baseSeed = seedFromRoom(room);
  const wetL = new Float32Array(numSamples);
  const wetR = new Float32Array(numSamples);

  // (a) Modal band → its own mono buffer so we can normalize it independently.
  const modal = new Float32Array(numSamples);
  buildModalMono(modal, room, sampleRate, mulberry32((baseSeed ^ 0x5a5a5a5a) >>> 0));

  const modalTrim = Math.pow(10, (opts.modalTrimDb ?? 0) / 20);
  let modalScale: number;
  if (opts.modalScale !== undefined) {
    modalScale = opts.modalScale * modalTrim;
  } else {
    const measured = rms(modal);
    modalScale = measured > 1e-30 ? (modalRmsTarget(room) / measured) * modalTrim : 0;
  }
  // Coherent LF: same modal signal in both ears.
  for (let i = 0; i < numSamples; i++) {
    const v = modalScale * modal[i];
    wetL[i] += v;
    wetR[i] += v;
  }

  // (b) Decorrelated diffuse tail.
  buildDiffuseTail(wetL, wetR, room, sampleRate, baseSeed);

  // Keep t=0 purely dry (real rooms have a gap before the first reflection).
  wetL[0] = 0;
  wetR[0] = 0;

  // (c) Energy-based dry/wet split to the target DRR, then a global peak cap.
  let sumWetSq = 0;
  for (let i = 0; i < numSamples; i++) sumWetSq += wetL[i] * wetL[i] + wetR[i] * wetR[i];

  let dryScale: number;
  let wetScale: number;
  if (sumWetSq < 1e-30) {
    dryScale = IR_PEAK_TARGET;
    wetScale = 0;
  } else {
    const ratio = Math.pow(10, drrForState(room.nProducts) / 10); // dryE / wetE
    const dryScaleRel = Math.sqrt((ratio * sumWetSq) / 2);
    let peak = dryScaleRel; // dry sits alone at sample 0 (wet was zeroed there)
    for (let i = 1; i < numSamples; i++) {
      const aL = Math.abs(wetL[i]);
      const aR = Math.abs(wetR[i]);
      if (aL > peak) peak = aL;
      if (aR > peak) peak = aR;
    }
    const finalScale = peak > 0 ? IR_PEAK_TARGET / peak : 0;
    dryScale = dryScaleRel * finalScale;
    wetScale = finalScale;
  }

  for (let i = 0; i < numSamples; i++) {
    left[i] = wetScale * wetL[i];
    right[i] = wetScale * wetR[i];
  }
  left[0] += dryScale;
  right[0] += dryScale;

  return { buffer, modalScale };
}
